import { useCallback } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { ConjugeUseCase } from '@/features/proposta/entities/Comprador/use-case/conjuge-use-case'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { showToast } from '@/infra/sonner'
import type { TCliente } from '@/types/cliente'

type TUseDadosConjugeProps = {
  refetch: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  onNext: () => void
  compradores: TCompradoresCadastrados
  nacionalidadePrincipalId: string
}

export function useDadosConjuge({ onNext, compradores, refetch, nacionalidadePrincipalId }: TUseDadosConjugeProps) {
  const { trigger, setValue, reset, watch, setError } = useFormContext<CompradorPessoaFisicaStepSchemaInput>()
  const compradorPrincipal = watch('dados')
  const cpfConjuge = watch('conjuge.dados.cpf') ?? ''
  const buscouCpf = watch('conjuge.dados.buscouCpf')
  const cpfRaw = cpfConjuge?.replaceAll(/\D/g, '')

  const useCase = new ConjugeUseCase(compradores, compradorPrincipal, nacionalidadePrincipalId)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok && error === 'MISSING_CPF') {
      trigger('conjuge.dados.cpf')
      return
    }

    if (!ok && error === 'NEED_SEARCH') {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
      })
      return
    }

    if (await trigger('conjuge.dados')) onNext()
  }, [cpfConjuge, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const validacaoCpf = useCase.validarCpfConjuge(cpfRaw)
    if (!validacaoCpf.ok) {
      const erroMap: Record<(typeof validacaoCpf)['reason'], string> = {
        SAME_AS_COMPRADOR: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_conjuge_comprador'),
        CONJUGE_ALREADY_LINKED: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_conjuge_vinculado'),
        IS_ALREADY_COMPRADOR: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_conjuge_e_comprador'),
      }

      setError('conjuge.dados.cpf', {
        message: erroMap[validacaoCpf.reason],
      })
      return
    }

    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        setValue('conjuge', fetchResult.patch)
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('conjuge.dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw])

  const handleLimpar = useCallback(() => {
    setValue('conjuge', useCase.limparFormulario(), { shouldValidate: false })
  }, [reset])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
